import {
    DonationContext, DonationContextEntries,
    DonationContextProperties, DonationProductParameters,
    DonationQueryParams,
    DonationRhythm, AdditionalDocTypeParameters,
    DonationType, FormDataInput,
    FormSettings, ProductType, SponsorshipType,
    UserSettings
} from "../types";
import {ParsedUrlQuery} from "querystring";
import {
    CmChannelImpl,
    SosDonationCard, SosDonationCertificate,
    SosDonationForm, SosDonationProduct,
    SosDonationTool
} from "../../../../../packages/graphql-layer/src";
import {TFunction} from "i18next";
import {roundTo, splitToNumberArray, stringToNumber} from "../../../../utility/numberUtil";
import {splitAndTrim} from "../../../../utility/stringUtil";
import {stringToNumberArray} from "../../../../utility/enumUtil";
import {ProductState} from "../../../doctypes/P020DonationProduct/P020DonationProduct";

export const setDonationContextValues = (customDonation: any, item: SosDonationForm | SosDonationCard | CmChannelImpl | SosDonationTool, additionalPriorities?: string[]): DonationContextEntries => {
    if (!customDonation || !customDonation.donationContext) {
        let settings = item?.donationSettings?.default?.donation_context || {}
        additionalPriorities &&
        Object.entries(DonationContext).forEach(([key, value]) => {
            let donationContextPriorities = settings[key as DonationContext]["priorities"];
            settings[key as DonationContext]["priorities"] = additionalPriorities.concat(donationContextPriorities.filter((value: string) => !additionalPriorities.includes(value)));
        });
        return settings;
    }
    let donationContext: DonationContextEntries = {};
    Object.entries(customDonation.donationContext).forEach(([key, value]) => {
        donationContext[key as DonationContext] = createContext(value, item?.donationSettings?.default?.donation_context?.[key], item, additionalPriorities);
    });
    return donationContext;
}
export const setDonationContextValuesForProduct = (item: any, donationProduct: SosDonationProduct, queryParams: ParsedUrlQuery): DonationContextEntries => {
    const productContext = item?.donationSettings?.default?.donation_context?.product || {}
    const productId = queryParams[DonationQueryParams.contentIdForProduct] as string;
    const productArticleNumber = donationProduct?.articleNumber || "CMS-ID: " + productId;
    productContext.priorities = [productArticleNumber];
    return productContext;
}
export const createProductParameters = (item: SosDonationProduct, queryParams: ParsedUrlQuery, productState?: ProductState): DonationProductParameters => {
    const productId = queryParams[DonationQueryParams.contentIdForProduct] as string;
    const productQuantity = productState?.productQuantity.toString();
    const productGreeting = productState?.productGreeting;
    const productFirstname = productState?.productFirstname;
    const productSurname = productState?.productSurname;
    const total = calculateProductTotal(item, stringToNumber(productQuantity));
    const productArticleNumber = item?.articleNumber || "CMS-ID: " + productId;
    return {
        productId: productId,
        productGreeting: productGreeting,
        productQuantity: stringToNumber(productQuantity),
        productFirstname: productFirstname,
        productSurname: productSurname,
        productTotal: total,
        productCertificateId: item.donationCertificate && item.donationCertificate.length > 0 ? (item.donationCertificate[0] as SosDonationCertificate).id : undefined,
        productArticleNumber: productArticleNumber
    } as DonationProductParameters
}
export const createAdditionalDocTypeParameters = (item: SosDonationForm | SosDonationCard | SosDonationTool | CmChannelImpl, queryParams: ParsedUrlQuery) => {
    if (item.type == "SOSDonationTool") {
        const sosDonationTool = item as SosDonationTool;
        return {
            actionId: sosDonationTool.actionId
        } as AdditionalDocTypeParameters
    }
    if (queryParams[DonationQueryParams.contentIdForSettings]) {
        return {
            cmCsId: queryParams[DonationQueryParams.contentIdForSettings] as string
        } as AdditionalDocTypeParameters
    }
}

export const createContext = (customSettings: any, defaultSettings: any, item: SosDonationForm | SosDonationCard | CmChannelImpl | SosDonationTool, additionalPriorities?: string[] | undefined): DonationContextProperties => {
    const sosDonationForm = extractSosDonationForm(item) as SosDonationForm;
    const amounts = (customSettings.amounts && splitToNumberArray(customSettings.amounts)) || defaultSettings?.amounts;
    return {
        headline: customSettings.headline || (sosDonationForm && sosDonationForm?.subheading1) || defaultSettings?.headline,
        subheadline: customSettings.subheadline || defaultSettings?.subheadline,
        amounts: amounts,
        description: customSettings.description || defaultSettings?.description,
        giftLabel: customSettings.giftLabel || defaultSettings?.giftLabel,
        minAmount: (customSettings.minAmount && parseInt(customSettings.minAmount)) || defaultSettings?.minAmount,
        priorities: createPriorities(customSettings.priorities, defaultSettings?.priorities, additionalPriorities),
        priorityLabel: customSettings.priorityLabel || defaultSettings?.priorityLabel,
        recipientDataIntroText: customSettings.recipientDataIntroText || defaultSettings?.recipientDataIntroText || "",
        defaultAmount: (customSettings.defaultAmount && parseInt(customSettings.defaultAmount)) || defaultSettings?.defaultAmount || amounts[1],
        pdfCertificate: defaultSettings?.pdfCertificate || null,
        buttonLabel: customSettings.buttonLabel || defaultSettings?.buttonLabel,
    };
}

export const extractSosDonationForm =(item: SosDonationForm | SosDonationTool)=> {
    if (item.type == "SOSDonationForm") {
        return  item as SosDonationForm;
    }
    if (item.type == "SOSDonationTool") {
        const sosDonationTool = item as SosDonationTool;
        return sosDonationTool!.donationForm![0];
    }
    return null;
}

export const createFormSettings = (item: SosDonationForm | SosDonationCard | CmChannelImpl | SosDonationTool, t: TFunction): FormSettings | null => {
    let sosDonationForm = extractSosDonationForm(item) as SosDonationForm;

    if (sosDonationForm == null) {
        return null;
    }
    return {
        formHeadline: sosDonationForm?.subheading1 || t("donation_form.headline"),
        paymentHeadline: sosDonationForm.subheading2 || t("donation_form.accountSubline"),
        personalDataHeadline: sosDonationForm.subheading3 || t("donation_form.addressSubline"),
        optionalLabel: sosDonationForm?.optionalLabel || t("address_form.optional_information"),
        newsletterAgree: sosDonationForm.newsletterAgree || "",
        submit_hint: sosDonationForm.donationSettings?.default?.submit_hint,
        payment_hint: sosDonationForm.donationSettings?.default?.payment_hint,
        payment_method: sosDonationForm.donationSettings?.default?.payment_method,
        agreementText: sosDonationForm.donationSettings?.default?.agreementText,
        privacyUrl: sosDonationForm.donationSettings?.default?.privacyUrl,
        saferPayAccount: sosDonationForm.saferPayAccount || "",
    }
}

export const createFormInput = (item: any, queryParams: ParsedUrlQuery, t: TFunction, additionalPriorities?: string[], donationProduct?: SosDonationProduct): FormDataInput => {
    const isProduct = donationProduct != null;
    return {
        productType: isProduct ? createProductTypeForProduct() : item.donationSettings?.custom.productType && createProductType(item.donationSettings?.custom.productType) || null,
        donationContext: isProduct ? setDonationContextValuesForProduct(item, donationProduct, queryParams) : setDonationContextValues(item.donationSettings?.custom, item, additionalPriorities),
        formSettings: createFormSettings(item, t),
        userSettings: createUserSettings(queryParams),
        additionalDocTypeParameters: createAdditionalDocTypeParameters(item, queryParams)
    } as FormDataInput
}

export const createProductType = (productTypeSettings: any): ProductType => {
    return {
        donationType: productTypeSettings ? stringToNumberArray(productTypeSettings.donationType) : [DonationType.Donation],
        donationRhythm: productTypeSettings ? stringToNumberArray(productTypeSettings.donationRhythm) : [DonationRhythm.Unique],
        sponsorshipType: productTypeSettings ? stringToNumberArray(productTypeSettings.sponsorshipType) : undefined,
    }
}

export const createProductTypeForProduct = () => {
    return {
        donationType: [DonationType.Product],
        donationRhythm: [DonationRhythm.Unique],
        sponsorshipType: undefined,
    }
}

export const createUserSettings = (queryParams: ParsedUrlQuery) => {
    const userSettings: UserSettings = {}

    queryParams[DonationQueryParams.amount] && (userSettings.amount = parseFloat(queryParams[DonationQueryParams.amount] as string));
    queryParams[DonationQueryParams.donationType] && (userSettings.donationType = parseInt(queryParams[DonationQueryParams.donationType] as string) as DonationType);
    queryParams[DonationQueryParams.donationRhythm] && (userSettings.donationRhythm = parseInt(queryParams[DonationQueryParams.donationRhythm] as string) as DonationRhythm);
    queryParams[DonationQueryParams.sponsorshipType] && (userSettings.sponsorshipType = parseInt(queryParams[DonationQueryParams.sponsorshipType] as string) as SponsorshipType);
    queryParams[DonationQueryParams.priority] && (userSettings.priority = decodeURIComponent(queryParams[DonationQueryParams.priority] as string));
    queryParams[DonationQueryParams.gift] && (userSettings.gift = decodeURIComponent(queryParams[DonationQueryParams.gift] as string));
    if (queryParams[DonationQueryParams.contentIdForProduct]) {
        userSettings.sponsorshipType = undefined;
    }
    return userSettings;
}


export const createPriorities = (
    customPriorities: string,
    defaultPriorities: any,
    additionalPriorities: string[] = []
) => {
    additionalPriorities = Array.isArray(additionalPriorities) ? additionalPriorities : [];
    const settingsPriorities = customPriorities
        ? splitAndTrim(customPriorities)
        : (Array.isArray(defaultPriorities) ? defaultPriorities : []);

    return additionalPriorities.concat(
        settingsPriorities.filter((value: string) => !additionalPriorities.includes(value))
    );
};

export function getAdditionalPriorities(pageContext: any, queryParams: ParsedUrlQuery) {
    if (queryParams[DonationQueryParams.priority]) {
        return [decodeURIComponent(queryParams[DonationQueryParams.priority] as string)];
    }
    if (pageContext.taxonomies?.length > 0) {
        return additionalPriorities(pageContext);
    }
}

const additionalPriorities = (pageContext: any) => {
    let prios: string[] = [];
    const taxonomies = pageContext.taxonomies;
    if (taxonomies) {
        taxonomies.map((name: any) => (
            prios.push(name)));
    }
    return prios;
}

function calculateProductTotal(item: SosDonationProduct, productQuantity: number) {
    const price = stringToNumber(item.price || "0");
    return roundTo(productQuantity * price, 2);
}